import { AxiosResponse } from 'axios';
import ApiService from './api';
import { Session, QueryParams, ChatReports, TableSession, SessionTimeline } from 'shared/types';
import { secondsTransform } from 'utils/date-utils';

// RFC 4180: wrap a field in quotes if it contains a comma, quote, or newline,
// and double any inner quote. Returns '' for null/undefined.
const csvEscape = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export const getSessions = async (
  page: number,
  limit: number,
  orderBy: { orderBy?: string },
  queryParams: QueryParams
): Promise<AxiosResponse<Session[]>> => {
  if (queryParams.userEmailIds && Array.isArray(queryParams.userEmailIds)) {
    if (queryParams.userEmailIds.length <= 0) {
      queryParams.userEmailIds = undefined
    } 
    else {
      if (queryParams.userIds && Array.isArray(queryParams.userIds)) {
        queryParams.userIds = [...queryParams.userIds, ...queryParams.userEmailIds]
      }
    }
  }
  if (queryParams.userIds && Array.isArray(queryParams.userIds)) {
    if (queryParams.userIds.length <= 0) {
      queryParams.userIds = undefined;
    }
    else {
      queryParams.userIds = queryParams.userIds.join(',')
    }
  }
  return await ApiService.get<Session[]>('sessions', { page, limit, ...orderBy, ...queryParams });
}


export const getSessionTimeline = async (
  sessionId: string,
): Promise<AxiosResponse<SessionTimeline>> =>
  await ApiService.get<SessionTimeline>(`sessions/${sessionId}/timeline`);

export const reassignSessionUser = async (
  sessionId: string,
  userId: string,
): Promise<AxiosResponse<{ success: boolean }>> =>
  await ApiService.patch<{ userId: string }, { success: boolean }>(`sessions/${sessionId}/reassign-user`, { userId });

export const deleteSession = async (sessionId: string): Promise<AxiosResponse<void>> =>
  await ApiService.delete<void>(`sessions/${sessionId}`);

export const downloadAnswerReports = async (session: TableSession) => {
  try {
    const reports = session.answerReports ?? [];
    const users = session.users;

    let csvData =
      [
        'Student name',
        'Room',
        'LE Name',
        'Station',
        'Answer entered',
        'Correct?',
        'UTC Timestamp'
      ].join(',') + '\r\n';

    reports.forEach((report) => {
      const r: any = report;
      const reportUserId = r.userId ?? r.user_id;
      const matchedUser = users.find(user => user.id === reportUserId);
      const fullName = matchedUser
        ? `${matchedUser.first_name ?? ''} ${matchedUser.last_name ?? ''}`.trim()
        : '';
      const userFullName = fullName || matchedUser?.email || 'Unknown';

      const learningExperienceId = r.learningExperienceId ?? r.learning_experience_id ?? '';
      const learningExperienceName =
        r.learningExperienceName ?? r.learning_experiences?.display_name ?? r.learning_experiences?.name ?? (learningExperienceId ? `LE ${learningExperienceId}` : '');
      const stationNumber = r.stationNumber ?? r.station_number ?? '';
      const userAnswer = r.userAnswer ?? r.user_answer ?? '';
      const isCorrect = r.isCorrect ?? r.is_correct;
      const answeredAt = r.answeredAt ?? r.answered_at ?? '';

      csvData +=
        [
          csvEscape(userFullName),
          csvEscape(session.roomId),
          csvEscape(learningExperienceName),
          csvEscape(stationNumber),
          csvEscape(userAnswer),
          csvEscape(isCorrect ? 'Correct' : 'Incorrect'),
          csvEscape(answeredAt)
        ].join(',') + '\r\n';
    });
    const BOM = '\uFEFF'; // UTF-8 Byte Order Mark
    const csvContent = BOM + csvData; // Prepend BOM to CSV data
    const blob = new Blob([csvContent]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `answers-reports_${session.id}.csv`;
    link.click();
  } catch (error) {
    console.error(error);
  }
};

export const downloadProgressReports = async (session: TableSession) => {
  try {
    const reports = session.progressReports ?? [];
    const users = session.users;

    let csvData =
      [
        'Student name',
        'Org',
        'Room',
        'LE Name',
        'Last Station completed',
        'UTC Timestamp of last station completion',
        'Time Spent In LE',
        'UTC Timestamp of Start/enter',
        'UTC Timestamp of End/exit'
      ].join(',') + '\r\n';

    reports.forEach((report) => {
      const r: any = report;
      const reportUserId = r.userId ?? r.user_id;
      const matchedUser = users.find(user => user.id === reportUserId);
      const fullName = matchedUser
        ? `${matchedUser.first_name ?? ''} ${matchedUser.last_name ?? ''}`.trim()
        : '';
      const userFullName = fullName || matchedUser?.email || 'Unknown';

      const learningExperienceId = r.learningExperienceId ?? r.learning_experience_id ?? '';
      const learningExperienceName =
        r.learningExperienceName ?? r.learning_experiences?.display_name ?? r.learning_experiences?.name ?? (learningExperienceId ? `LE ${learningExperienceId}` : '');
      const lastCompletedStation = r.lastCompletedStation ?? r.last_completed_station;
      const lastCompletedStationAt = r.lastCompletedStationAt ?? r.last_completed_station_at ?? '';
      const enteredAt = r.enteredLearningExperienceAt ?? r.entered_learning_experience_at;
      const exitedAt = r.exitedLearningExperienceAt ?? r.exited_learning_experience_at;

      const enteredSec = enteredAt ? new Date(enteredAt).getTime() : 0;
      const exitedSec = exitedAt ? new Date(exitedAt).getTime() : 0;
      const durationSeconds = enteredSec && exitedSec
        ? Math.floor(Math.abs(exitedSec - enteredSec) / 1000)
        : 0;

      csvData +=
        [
          csvEscape(userFullName),
          csvEscape(session.school),
          csvEscape(session.roomId),
          csvEscape(learningExperienceName),
          csvEscape(!!lastCompletedStation && lastCompletedStation > 0
            ? lastCompletedStation
            : 'Intro Station'),
          csvEscape(lastCompletedStationAt),
          csvEscape(secondsTransform(durationSeconds)),
          csvEscape(enteredAt),
          csvEscape(exitedAt)
        ].join(',') + '\r\n';
    });
    const BOM = '\uFEFF'; // UTF-8 Byte Order Mark
    const csvContent = BOM + csvData; // Prepend BOM to CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `progress-reports_${session.id}.csv`;
    link.click();
  } catch (error) {
    console.error(error);
  }
};

export const downloadChatReports = async (sessionId: string, reports: ChatReports[]) => {
  try {
    let csvData =
      ['Name', 'Room', 'LE Name', 'What was said', 'UTC Timestamp', 'Scripted or Q&A'].join(',') +
      '\r\n';

    reports.forEach((report) => {
      csvData +=
        [
          report.userName,
          report.roomId,
          `"${report.learningExperienceName}"`,
          `"${report.whatWasSaid}"`,
          report.timestamp,
          report.scriptedOrQA
        ].join(',') + '\r\n';
    });

    const BOM = '\uFEFF'; // UTF-8 Byte Order Mark
    const csvContent = BOM + csvData; // Prepend BOM to CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `chat-log-reports_${sessionId}.csv`;
    link.click();
  } catch (error) {
    console.error(error);
  }
};

export const downloadAllSessionProgressReports = async (
  queryParams: QueryParams
): Promise<void> => {
  try {
    const response = await ApiService.get<Blob>(`sessions/export`, { ...queryParams });

    const contentDisposition = response.headers['content-disposition'];

    const matches = /filename="(.+)"/.exec(contentDisposition);
    const filename =
      matches && matches[1] ? matches[1] : `session_progress_reports_${Date.now()}.csv`;

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading the CSV file:', error);
    throw error;
  }
};
